<template>
  <div>
    <SectionContainer v-if="showSimilarSpecialists" bg-color="light">
      <SHeading centered level="2" size="2">
        {{
          tr(
            "main_site.company_wall.details.internal_links.similar_specialists_title",
          )
        }}
      </SHeading>
      <p class="company-wall-details-internal-links__description">
        {{
          tr(
            "main_site.company_wall.details.internal_links.similar_specialists_description",
            { municipality: municipalityName },
          )
        }}
      </p>
      <div class="company-wall-details-internal-links__company-list">
        <template v-if="similarSpecialists.length">
          <CompanyWallMunicipalityCard
            v-for="company in similarSpecialists"
            :key="company.companyCard.id"
            :company
            :geo-product-slug="productSlug ?? ''"
          />
        </template>
        <template v-else-if="companiesState.status === 'pending'">
          <CompanyWallMunicipalityCardSkeleton v-for="i in 4" :key="i" />
        </template>
      </div>
      <SErrorMessage
        v-if="companiesState.status === 'error'"
        retry
        size="lg"
        @retry="companiesRefresh"
      />
    </SectionContainer>

    <SectionContainer v-if="showOtherProjects" size="md">
      <SHeading centered level="2" size="2">
        {{
          tr(
            "main_site.company_wall.details.internal_links.other_projects_title",
            { municipality: municipalityName },
          )
        }}
      </SHeading>
      <p class="company-wall-details-internal-links__description">
        {{
          tr(
            "main_site.company_wall.details.internal_links.other_projects_description",
            { municipality: municipalityName },
          )
        }}
      </p>
      <SButtonList
        v-if="otherProjects?.length"
        :items="otherProjects"
        :max-items="12"
        render-all
        :show-more-label="
          tr('main_site.company_wall.details.internal_links.show_more')
        "
        :tag="NuxtLink"
      />
      <SButtonListSkeleton v-else />
    </SectionContainer>

    <SectionContainer v-if="showOtherMunicipalities" size="md">
      <SHeading centered level="2" size="2">
        {{
          tr(
            "main_site.company_wall.details.internal_links.other_regions_title",
          )
        }}
      </SHeading>
      <p class="company-wall-details-internal-links__description">
        {{
          tr(
            "main_site.company_wall.details.internal_links.other_regions_description",
          )
        }}
      </p>
      <SButtonList
        v-if="otherMunicipalities?.length"
        :items="otherMunicipalities"
        :max-items="12"
        prefix-icon="far fa-location-dot"
        render-all
        :show-more-label="
          tr('main_site.company_wall.details.internal_links.show_more')
        "
        :tag="NuxtLink"
      />
      <SButtonListSkeleton v-else />
    </SectionContainer>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { NuxtLink } from "#components";
import { computed, toRef } from "vue";

import {
  SButtonList,
  SButtonListSkeleton,
  SErrorMessage,
  SHeading,
  useGeoProductCategories,
} from "@solvari/components";
import {
  injectI18nLocale,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import { ziggy } from "@/shared/libs/ziggy";
import {
  useMunicipalities,
  useMunicipality,
} from "~/features/company-wall/api/geo.api.ts";
import { useCompanyCardsLocalSearchTop } from "~/features/company-wall/api/localSearch.api.ts";
import CompanyWallMunicipalityCard from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityCard.vue";
import CompanyWallMunicipalityCardSkeleton from "~/features/company-wall/components/company-wall-municipality/CompanyWallMunicipalityCardSkeleton.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

const props = defineProps<{
  companyId: number;
  municipalityName?: string;
  municipalitySlug?: string;
  productSlug?: string;
  provinceSlug?: string;
}>();

const localeCode = injectI18nLocale().value;
const { tr } = useI18nMainSiteCompanyWall();

const { data: municipalityData } = useQuery({
  ...useMunicipality({ localeCode, slug: props.municipalitySlug ?? "" }),
  enabled: () => !!props.municipalitySlug && !props.municipalityName,
});

const municipalityName = computed(
  () =>
    props.municipalityName ??
    municipalityData.value?.name ??
    props.municipalitySlug ??
    "",
);

const { state: companiesState, refresh: companiesRefresh } = useQuery({
  ...useCompanyCardsLocalSearchTop({
    geoProductCategorySlug: toRef(() => props.productSlug ?? ""),
    localeCode,
    municipalitySlug: toRef(() => props.municipalitySlug ?? ""),
    limit: 5,
  }),
  enabled: () => !!props.productSlug && !!props.municipalitySlug,
});

const similarSpecialists = computed(() => {
  if (!companiesState.value.data?.data) {
    return [];
  }
  return companiesState.value.data.data
    .filter((company) => company.companyCard.id !== props.companyId)
    .slice(0, 4);
});

const showSimilarSpecialists = computed(() => {
  return (
    !!props.productSlug &&
    !!props.municipalitySlug &&
    (similarSpecialists.value.length > 0 ||
      companiesState.value.status !== "success")
  );
});

const { data: productsData, status: productsStatus } = useQuery(
  useGeoProductCategories(
    { localeCode, hasLocalSearch: true },
    handleNuxtQuery,
  ),
);

const otherProjects = computed(() => {
  const { municipalitySlug, provinceSlug, productSlug } = props;
  if (!productSlug || !municipalitySlug || !provinceSlug) {
    return [];
  }
  return productsData.value
    ?.filter((product) => product.slug !== productSlug)
    .map((product) => ({
      name: product.name,
      to: ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: product.slug,
        province: provinceSlug,
        municipality: municipalitySlug,
      }),
    }));
});

const showOtherProjects = computed(() => {
  return (
    !!props.productSlug &&
    !!props.municipalitySlug &&
    !!props.provinceSlug &&
    (!!otherProjects.value?.length || productsStatus.value === "pending")
  );
});

const { data: municipalitiesData, status: municipalitiesStatus } =
  useMunicipalities(
    { provinceSlug: props.provinceSlug ?? null, localeCode },
    { enabled: () => !!props.provinceSlug },
  );

const otherMunicipalities = computed(() => {
  const { municipalitySlug, provinceSlug, productSlug } = props;
  if (!provinceSlug || !productSlug) {
    return [];
  }
  return municipalitiesData.value
    ?.filter((municipality) => municipality.slug !== municipalitySlug)
    .map((municipality) => ({
      ...municipality,
      to: ziggy("mainSite.localSearch.municipality", {
        geoProductSlug: productSlug,
        province: provinceSlug,
        municipality: municipality.slug,
      }),
    }));
});

const showOtherMunicipalities = computed(() => {
  return (
    !!props.productSlug &&
    !!props.provinceSlug &&
    (!!otherMunicipalities.value?.length ||
      municipalitiesStatus.value === "pending")
  );
});
</script>

<style lang="postcss">
.company-wall-details-internal-links {
  &__description {
    @apply mb-6 text-center text-lg text-slate-700;
  }

  &__company-list {
    @apply grid gap-6 grid-cols-auto-104 grid-cols-auto-grow;
  }
}
</style>
